import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { InfoTip } from "@/components/InfoTip";
import { STATUS_META, REJECT_CONFIRM } from "@/lib/pipeline";
import { Briefcase, Plus, Trash2, Loader2 } from "lucide-react";

const BY = { cv: "podľa CV", ai: "podľa AI", manual: "manuálne" };
const POS_STATUSES = ["new", "in_process", "interview", "hired", "rejected_retain", "rejected_shredded"];

export function PositionsPanel({ p, onChanged }) {
  const [busy, setBusy] = useState(null);
  const positions = p.positions || [];

  const setStatus = async (pos, status) => {
    if (status === pos.status) return;
    if (status === "rejected_shredded" && !window.confirm(REJECT_CONFIRM)) return;
    setBusy(pos.id);
    try {
      const r = await api.put(`/company/candidate/${p.id}/positions/${pos.id}`, { status });
      if (r.data.shredded) toast.success("Kandidát zamietnutý – údaje skartované a presunuté do GDPR");
      else if (r.data.needs_consent) toast.info("Presunuté do Talent Pool – vyžiadajte súhlas na uchovanie v sekcii GDPR.");
      else toast.success(`${pos.name}: ${STATUS_META[status].l}`);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const remove = async (pos) => {
    if (!window.confirm(`Odobrať pozíciu „${pos.name}"?`)) return;
    try { await api.delete(`/company/candidate/${p.id}/positions/${pos.id}`); await onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="positions-section">
      <h3 className="font-head font-semibold flex items-center gap-2 text-[#1e2a5a]"><Briefcase size={17} className="text-violet-600" /> Pozície a stav
        <InfoTip text="Kandidát môže byť zaradený na viac pozícií. Každá pozícia má vlastný stav. Zamietnutie na ktorejkoľvek pozícii skartuje celý profil." /></h3>
      <ul className="mt-3 space-y-2" data-testid="positions-list">
        {positions.length === 0 && <li className="text-xs text-slate-400">Kandidát zatiaľ nie je zaradený na žiadnu pozíciu.</li>}
        {positions.map((pos) => (
          <li key={pos.id} data-testid={`position-${pos.id}`} className="rounded-2xl border border-slate-200 p-3">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate">{pos.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{pos.area}{pos.assigned_by ? ` · ${BY[pos.assigned_by] || ""}` : ""}</div>
              </div>
              <button data-testid={`position-remove-${pos.id}`} onClick={() => remove(pos)} aria-label="Odobrať"
                className="h-7 w-7 flex items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <select data-testid={`position-status-${pos.id}`} value={pos.status} disabled={busy === pos.id}
                onChange={(e) => setStatus(pos, e.target.value)}
                className={`flex-1 rounded-full border px-3 py-1.5 text-xs font-semibold outline-none ${(STATUS_META[pos.status] || STATUS_META.new).pill}`}>
                {POS_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].l}</option>)}
              </select>
              {busy === pos.id && <Loader2 size={14} className="animate-spin text-violet-600" />}
            </div>
          </li>
        ))}
      </ul>
      <AddPosition profileId={p.id} onAdded={onChanged} />
    </div>
  );
}

export function useCatalog() {
  const [cat, setCat] = useState({ areas: [], catalog: {} });
  useEffect(() => { api.get("/company/db/catalog").then((r) => setCat(r.data)).catch(() => {}); }, []);
  return cat;
}

export function AddPosition({ profileId, onAdded, assignedBy = "manual", initialName = "", initialArea = "" }) {
  const { areas, catalog } = useCatalog();
  const [area, setArea] = useState(initialArea);
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setArea(initialArea); setName(initialName); }, [initialArea, initialName]);
  const options = area ? catalog[area] || [] : Object.values(catalog).flat();
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/company/candidate/${profileId}/positions`, { name: name.trim(), area: area || null, assigned_by: assignedBy });
      toast.success(`Zaradený: ${r.data.position.name} (${r.data.position.area})`);
      setName(""); await onAdded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid="add-position">
      <select data-testid="add-position-area" value={area} onChange={(e) => setArea(e.target.value)}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500">
        <option value="">Oblasť automaticky podľa pozície</option>
        {areas.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      <div className="flex gap-2">
        <input data-testid="add-position-name" list={`pos-list-${profileId}`} value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Pozícia (zo zoznamu alebo vlastná)…"
          className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
        <datalist id={`pos-list-${profileId}`}>{options.map((o) => <option key={o} value={o} />)}</datalist>
        <button data-testid="add-position-btn" onClick={add} disabled={busy || !name.trim()}
          className="inline-flex items-center gap-1 rounded-xl bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50 transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Zaradiť
        </button>
      </div>
    </div>
  );
}
